import React, { useEffect, useState } from "react";
import { adminBlogApi } from "../../lib/blogApi";
import { adminApi } from "../../lib/api";
import { resolveAsset } from "../../lib/assetUrl";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Card, CardContent } from "../../components/ui/card";
import { Switch } from "../../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Plus, Edit, Trash2, Eye, EyeOff, Upload, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const emptyPost = {
  slug: "",
  title: "",
  subtitle: "",
  excerpt: "",
  cover_image: "",
  category: "3D Printing",
  tags: [],
  author: "Abhijeet Makan",
  content: "",
  read_minutes: 5,
  published: true,
};

const slugify = (s) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 80);

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPost);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const token = localStorage.getItem("admin_token") || "";

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminBlogApi.list(token);
      setPosts(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load posts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyPost);
    setDialogOpen(true);
  };

  const openEdit = async (post) => {
    // Listing endpoint omits content - fetch full post via public endpoint
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/blog/posts/${post.slug}`
      );
      if (res.ok) {
        const full = await res.json();
        setEditing(post);
        setForm({ ...emptyPost, ...full, tags: full.tags || [] });
      } else {
        // Fallback for unpublished posts: edit with what we have, blank content
        setEditing(post);
        setForm({ ...emptyPost, ...post, tags: post.tags || [], content: "" });
      }
    } catch {
      setEditing(post);
      setForm({ ...emptyPost, ...post, tags: post.tags || [], content: "" });
    }
    setDialogOpen(true);
  };

  const handleTitleChange = (value) => {
    setForm((f) => ({
      ...f,
      title: value,
      slug: editing ? f.slug : slugify(value),
    }));
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await adminApi.uploadImage(file);
      setForm((f) => ({ ...f, cover_image: res.data.url }));
      toast.success("Cover uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.slug.trim()) {
      toast.error("Title and slug are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: Array.isArray(form.tags)
          ? form.tags
          : String(form.tags || "")
              .split(",")
              .map((t) => t.trim())
              .filter(Boolean),
        read_minutes: Number(form.read_minutes) || 5,
      };
      if (editing) {
        await adminBlogApi.update(editing.id, payload, token);
        toast.success("Post updated");
      } else {
        await adminBlogApi.create(payload, token);
        toast.success("Post created");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      await adminBlogApi.remove(post.id, token);
      toast.success("Post deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Blog Posts</h1>
          <p className="text-sm text-slate-500 mt-1">
            Write, edit and publish articles. {posts.length} post{posts.length !== 1 ? "s" : ""} total.
          </p>
        </div>
        <Button
          onClick={openCreate}
          data-testid="new-post-btn"
          className="bg-sky-500 hover:bg-sky-600 text-white"
        >
          <Plus className="w-4 h-4 mr-1.5" />
          New Post
        </Button>
      </div>

      {loading ? (
        <div className="py-20 text-center text-slate-400">Loading…</div>
      ) : posts.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-500">
            No posts yet. Click "New Post" to write your first article.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {posts.map((p) => (
            <Card key={p.id} className="border-slate-100">
              <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="w-20 h-20 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                  {p.cover_image && (
                    <img
                      src={resolveAsset(p.cover_image)}
                      alt={p.title}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold text-sky-600 uppercase tracking-wider">
                      {p.category || "Uncategorized"}
                    </span>
                    {p.published ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                        <Eye className="w-3 h-3" />
                        Published
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                        <EyeOff className="w-3 h-3" />
                        Draft
                      </span>
                    )}
                  </div>
                  <h3 className="text-base font-semibold text-slate-900 truncate">{p.title}</h3>
                  <p className="text-sm text-slate-500 truncate">/blog/{p.slug}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <a
                    href={`/blog/${p.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-sky-600 transition-colors"
                    title="Preview"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  <button
                    onClick={() => openEdit(p)}
                    data-testid={`edit-post-${p.slug}`}
                    className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-sky-600 transition-colors"
                    title="Edit"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(p)}
                    data-testid={`delete-post-${p.slug}`}
                    className="p-2 rounded-lg text-slate-500 hover:bg-red-50 hover:text-red-600 transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Post" : "New Post"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div>
              <Label>Title *</Label>
              <Input
                value={form.title}
                onChange={(e) => handleTitleChange(e.target.value)}
                placeholder="A great article title"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Slug (URL) *</Label>
                <Input
                  value={form.slug}
                  onChange={(e) => setForm((f) => ({ ...f, slug: slugify(e.target.value) }))}
                  placeholder="great-article"
                />
              </div>
              <div>
                <Label>Category</Label>
                <Input
                  value={form.category}
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                  placeholder="3D Printing"
                />
              </div>
            </div>

            <div>
              <Label>Subtitle</Label>
              <Input
                value={form.subtitle}
                onChange={(e) => setForm((f) => ({ ...f, subtitle: e.target.value }))}
              />
            </div>

            <div>
              <Label>Excerpt (short preview shown on listing)</Label>
              <Textarea
                value={form.excerpt}
                onChange={(e) => setForm((f) => ({ ...f, excerpt: e.target.value }))}
                rows={2}
              />
            </div>

            <div>
              <Label>Cover Image</Label>
              <div className="flex items-center gap-3 mt-1">
                <Input
                  value={form.cover_image}
                  onChange={(e) => setForm((f) => ({ ...f, cover_image: e.target.value }))}
                  placeholder="https://... or upload below"
                />
                <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-2 border border-slate-200 rounded-md hover:bg-slate-50 text-sm">
                  <Upload className="w-4 h-4" />
                  {uploading ? "Uploading…" : "Upload"}
                  <input type="file" accept="image/*" className="hidden" onChange={handleUpload} />
                </label>
              </div>
              {form.cover_image && (
                <img
                  src={resolveAsset(form.cover_image)}
                  alt="cover preview"
                  className="mt-2 w-full max-h-48 object-cover rounded-lg"
                />
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Author</Label>
                <Input
                  value={form.author}
                  onChange={(e) => setForm((f) => ({ ...f, author: e.target.value }))}
                />
              </div>
              <div>
                <Label>Read time (minutes)</Label>
                <Input
                  type="number"
                  min={1}
                  value={form.read_minutes}
                  onChange={(e) => setForm((f) => ({ ...f, read_minutes: e.target.value }))}
                />
              </div>
            </div>

            <div>
              <Label>Tags (comma-separated)</Label>
              <Input
                value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    tags: e.target.value
                      .split(",")
                      .map((t) => t.trim())
                      .filter(Boolean),
                  }))
                }
                placeholder="blender, 3d printing, tutorial"
              />
            </div>

            <div>
              <Label>Content (Markdown)</Label>
              <Textarea
                value={form.content}
                onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
                rows={16}
                className="font-mono text-sm"
                placeholder="# Heading\n\nSome paragraph text. Use **bold**, *italic*, [links](https://example.com), and ## subheadings."
              />
              <p className="text-xs text-slate-400 mt-1">
                Supports Markdown: # H1, ## H2, **bold**, *italic*, lists, [links](#), tables.
              </p>
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
              <div>
                <Label className="cursor-pointer">Published</Label>
                <p className="text-xs text-slate-500 mt-0.5">
                  Drafts are hidden from the public blog and search engines.
                </p>
              </div>
              <Switch
                checked={form.published}
                onCheckedChange={(v) => setForm((f) => ({ ...f, published: v }))}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                data-testid="save-post-btn"
                className="bg-sky-500 hover:bg-sky-600 text-white"
              >
                {saving ? "Saving…" : editing ? "Update Post" : "Create Post"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
